import { useState, useEffect, useCallback } from 'react';
import { useAppSelector } from '@/app/store/store';
import type { StoreGiftCardService } from '../../application/services/StoreGiftCardService';
import type { GiftCardFormData, PhysicalGiftCardConfig } from '../../domain/models/storeGiftCard.types';
import {
  selectStoreGiftCardInitialConfig,
  STORE_GIFT_CARD_INITIAL_SLICE_NAME,
  type StoreGiftCardInitialState,
} from '../store/storeGiftCardInitialSlice';

export const useStoreGiftCard = (service: StoreGiftCardService) => {
  const ssrConfig = useAppSelector((state: { [STORE_GIFT_CARD_INITIAL_SLICE_NAME]?: StoreGiftCardInitialState }) =>
    selectStoreGiftCardInitialConfig(state)
  );
  const [config, setConfig] = useState<PhysicalGiftCardConfig | null>(ssrConfig);
  const [isConfigLoading, setIsConfigLoading] = useState<boolean>(!ssrConfig);
  const [configError, setConfigError] = useState<string | null>(null);
  
  const [isCheckingNdd, setIsCheckingNdd] = useState<boolean>(false);
  const [hasNddError, setHasNddError] = useState<boolean>(false);
  const [pincodeErrorMessage, setPincodeErrorMessage] = useState<string | null>(null);
  const [estimatedDeliveryDate, setEstimatedDeliveryDate] = useState<string | null>(null);

  // Mandatory initial config load (skipped when SSR already provided config)
  useEffect(() => {
    if (ssrConfig || config) return;

    const loadConfig = async () => {
      try {
        setIsConfigLoading(true);
        setConfigError(null);
        const data = await service.fetchGiftCardConfig();
        
        if (!data.customizationId) {
          throw new Error('Missing customization configuration');
        }
        
        setConfig(data);
      } catch {
        setConfigError('Unable to load gift card configuration. Please refresh and try again.');
        setConfig(null);
      } finally {
        setIsConfigLoading(false);
      }
    };

    loadConfig();
  }, [service, ssrConfig, config]);

  const resetPincodeState = useCallback(() => {
    setHasNddError(false);
    setPincodeErrorMessage(null);
    setEstimatedDeliveryDate(null);
  }, []);

  const checkPincodeManual = useCallback(
    async (pincode: string): Promise<boolean> => {
      try {
        setIsCheckingNdd(true);
        resetPincodeState();

        const nddResult = await service.checkNddAvailability(pincode);

        if (nddResult.hasError) {
          setHasNddError(true);
          setPincodeErrorMessage(
            `Sorry! Our third party delivery services do not deliver to the pincode (${pincode}). We are working on it. Please try an alternative pincode where the shipment can be delivered.`
          );
          return false;
        }

        if (nddResult.estimatedDeliveryDate) {
          setEstimatedDeliveryDate(nddResult.estimatedDeliveryDate);
        }
        return true;
      } catch {
        setHasNddError(true);
        setPincodeErrorMessage('Unable to verify pincode. Please try an alternative pincode.');
        return false;
      } finally {
        setIsCheckingNdd(false);
      }
    },
    [service, resetPincodeState]
  );

  const processOrder = useCallback(
    async (formData: GiftCardFormData) => {
      if (!config?.customizationId) {
        setConfigError('Configuration missing. Unable to proceed.');
        return;
      }

      service.submitGiftCardOrder(formData, config.customizationId);
    },
    [service, config]
  );

  return {
    config,
    isConfigLoading,
    configError,
    processOrder,
    checkPincodeManual,
    isCheckingNdd,
    hasNddError,
    pincodeErrorMessage,
    estimatedDeliveryDate,
    resetPincodeState,
  };
};